import Button from "../../button/Button";
import styles from "./ManageSubscription.module.scss";
import close from "../../../assets/icons/close.svg";
import payment from "../../../assets/icons/pay-method.svg";
import subImg from "../../../assets/icons/change-sub.svg";
import { useTranslation } from "react-i18next";
import { subscriptionData } from "../../../types/details-api-types";
import { formatPriceWithCurrency } from "../../../utils";
import arrowDown from "../../../assets/icons/arrow-down.svg";
import arrowUp from "../../../assets/icons/arrow-up.svg";
import { useEffect, useMemo, useState } from "react";
import { EVENT_NAMES } from "../../../types/event-names";
import { trackEvent } from "../../../utils/analytics";

interface ManageSubscriptionModalProps {
  setIsModalManageSubVisible: (arg: boolean) => void;
  setIsModalPauseSubVisible: (arg: boolean) => void;
  setIsModalUpdatePayVisible: (arg: boolean) => void;
  setIsCancelSubVisible: (arg: boolean) => void;
  setIsModalRenewalVisible?: (arg: boolean) => void;
  subscriptionItem?: subscriptionData | null;
}

const ManageSubscriptionModal: React.FC<ManageSubscriptionModalProps> = ({
  setIsModalManageSubVisible,
  setIsModalPauseSubVisible,
  setIsModalUpdatePayVisible,
  setIsCancelSubVisible,
  setIsModalRenewalVisible,
  subscriptionItem,
}) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);

  const toggle = () => {
    setIsOpen(!isOpen);

    trackEvent(EVENT_NAMES.SUBSCRIPTION_MANAGE_STATUS, {
      subscriptionId: subscriptionItem?.subscription_id,
      parameter: !isOpen ? "OPEN" : "CLOSE",
    });
  };

  const arrow = useMemo(() => {
    return !isOpen ? arrowDown : arrowUp;
  }, [isOpen]);

  const date = subscriptionItem?.next_payment ? new Date(subscriptionItem?.next_payment).toLocaleDateString() : "";

  useEffect(() => {
    trackEvent(EVENT_NAMES.SUBSCRIPTION_MANAGE_OPENED);
  }, []);


  // const searchParams = useMemo(() => new URLSearchParams(window.location.search), []);
  // const showFeature = searchParams.get("feature") === "1";

  const cardType = subscriptionItem?.paymentInformation?.card_type ?? "";


  return (
    <div className={styles.root}>
      <div className={styles.root_header}>
        <div>
          <p className={styles.root_header_title}>
            {t("Manage Your Subscription: ")}
            {subscriptionItem?.subscription_id}
          </p>
          <p className={styles.root_header_subtitle}>
            {t("Customize your Fortect subscription to ensure you continue enjoying our product.")}
          </p>
        </div>
        <img
          src={close}
          className={styles.root_header_img}
          alt="close"
          onClick={() => setIsModalManageSubVisible(false)}
        />
      </div>
      <div className={styles.root_main}>
        <div className={styles.root_main_payMethod}>
          <img src={payment} alt="payment" />
          <div>
            <p className={styles.root_main_payMethod_title}>{t("Payment Method")}</p>
            <p className={styles.root_main_payMethod_subtitle}>
              {t("Keep your payment information up-to-date to avoid any interruptions in your subscription.")}
            </p>
            <div className={styles.root_main_payMethod_card}>
              <p>****</p>
              <p>****</p>
              <p>****</p>
              <p>{subscriptionItem?.paymentInformation.last_four_digits}</p>
              <p>
                {cardType?.toLowerCase()?.includes("card") ? "Credit Card" : cardType?.toLocaleUpperCase()}
              </p>
            </div>
            <Button
              type="default"
              text={t("Update Payment Method")}
              onClick={() => {
                setIsModalManageSubVisible(false);
                setIsModalUpdatePayVisible(true);
              }}
              classNames={styles.root_main_payMethod_button}
            />
          </div>
        </div>
        <div className={styles.root_main_subscription}>
          <img src={subImg} alt="subImg" />
          <div className={styles.root_main_subscription_wrap}>
            <p className={styles.root_main_subscription_title}>{t("Change Subscription")}</p>
            <p className={styles.root_main_subscription_subtitle}>
              {t(
                "If you're thinking about pausing or canceling your subscription, remember our team is here to help. Let us know how we can improve your experience before making changes."
              )}
            </p>
            {subscriptionItem?.isActive && (
              <>
                <div className={styles.root_main_subscription_content_wrap}>
                  <p className={styles.root_main_subscription_content_wrap_toggle} onClick={() => toggle()}>
                    {t("Subscription Status")} <img src={arrow} alt="arrow" width={12} />
                  </p>
                  <div
                    className={styles.root_main_subscription_content_wrap_container}
                    style={{ height: isOpen ? "fit-content" : 0 }}
                  >
                    <div className={styles.root_main_subscription_buttons}>
                      {date && (
                        <p className={styles.root_main_subscription_title}>
                          {t("Renewal date")}: <span>{date}</span>
                        </p>
                      )}
                      {subscriptionItem?.renewalPrice && (
                        <p className={styles.root_main_subscription_title}>
                          {t("Renewal price")}:
                          <span>
                            {formatPriceWithCurrency(
                              subscriptionItem?.renewalPrice || "",
                              subscriptionItem?.renewalPriceCurrency || ""
                            )}
                          </span>
                        </p>
                      )}
                    </div>

                    <div className={styles.root_main_subscription_buttons}>
                      <Button
                        onClick={() => {
                          setIsModalPauseSubVisible(true);
                          setIsModalManageSubVisible(false);
                        }}
                        text={t("Pause Subscription")}
                        type="default"
                        classNames={styles.root_main_subscription_buttons_pause}
                      />
                      <div className={styles.root_main_subscription_link}>
                        <a
                          href="https://www.fortect.com/support/"
                          target="_blank"
                          rel="noreferrer"
                          onClick={() => trackEvent(EVENT_NAMES.SUBSCRIPTION_SUPPORT)}
                        >
                          {t("Contact support")}
                        </a>
                      </div>
                      <Button
                        onClick={() => {
                          setIsModalManageSubVisible(false);
                          setIsCancelSubVisible(true);
                        }}
                        text={t("Cancel Subscription")}
                        type="error"
                      />
                    </div>
                  </div>
                </div>
              </>
            )}

            {subscriptionItem?.isPaused && (
              <div className={styles.root_main_subscription_paused}>
                <div className={styles.root_main_subscription_paused_row}>
                  <p className={styles.root_main_subscription_paused_row_title}>{t("Renewal Status:")}</p>
                  <p className={`paused`}>{t("Paused")}</p>
                </div>
                <Button
                  onClick={() => {
                    setIsModalRenewalVisible!(true);
                    setIsModalManageSubVisible(false);
                  }}
                  text={t("Reactivate subscription")}
                  classNames={styles.root_main_subscription_paused_button}
                />
              </div>
            )}
          </div>
        </div>
      </div>
      <div className={styles.root_button}>
        <Button
          onClick={() => setIsModalManageSubVisible(false)}
          text={t("Close")}
          classNames={styles.root_button_close}
        />
      </div>
    </div>
  );
};

export default ManageSubscriptionModal;
